import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  Text,
} from "@chakra-ui/react";
import {
  AddCircleOutlined as AddCircleOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { useFormikContext } from "formik";
import { ChangeEvent, FC, useCallback, useRef, useState } from "react";

import { SymbolOutlineButton } from "~/src/components/common/Button";
import { IconButton } from "~/src/components/common/IconButton";
import { chakraFactory } from "~/src/lib/chakra-ui";

import {
  POPUP_VIDEO_MAX_BYTES,
  POPUP_VIDEO_MAX_SIZE_MB,
  uploadedVideoPreviewStyle,
} from "./popupVideoFormUtils";

type Props = {
  name: string;
  previewSrc?: string | null;
  onDelete: () => void;
};

const StyledInput = chakraFactory(Input, {
  baseStyle: {
    display: "none",
  },
});

const StyledLabel = chakraFactory(FormLabel, {
  baseStyle: {
    fontSize: "unset",
  },
});

const StyledVideo = chakraFactory("video", {
  baseStyle: uploadedVideoPreviewStyle,
});

export const VideoUploadField: FC<Props> = ({ name, previewSrc, onDelete }) => {
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);
  const { setFieldValue } = useFormikContext();

  const handleUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      try {
        const file = e?.target?.files && e.target.files[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith(".mp4")) {
          setError("MP4形式のファイルを選択してください");
          return;
        }

        if (file.size > POPUP_VIDEO_MAX_BYTES) {
          setError(
            `ファイルサイズは${POPUP_VIDEO_MAX_SIZE_MB}MB以下にしてください`
          );
          return;
        }

        setError(undefined);
        setPreviewUrl(URL.createObjectURL(file));
        setFieldValue(name, file, true);
      } catch (uploadError) {
        console.error("error", uploadError);
      }
    },
    [name, setFieldValue]
  );

  const handleDelete = useCallback(() => {
    setPreviewUrl(undefined);
    setError(undefined);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onDelete();
  }, [onDelete]);

  const videoSrc = previewUrl || previewSrc || undefined;

  return (
    <>
      <FormControl>
        <HStack justifyContent="space-between" alignItems="flex-start">
          <Box>
            <InputGroup>
              <StyledInput
                ref={inputRef}
                type="file"
                accept="video/mp4,.mp4"
                onChange={handleUpload}
              />
              <SymbolOutlineButton icon={<AddCircleOutlinedIcon />}>
                <StyledLabel margin="auto">MP4アップロード</StyledLabel>
              </SymbolOutlineButton>
            </InputGroup>
            {error && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {error}
              </Text>
            )}
          </Box>
          {videoSrc && (
            <IconButton
              size="24px"
              onClick={handleDelete}
              icon={<DeleteOutlineIcon />}
            />
          )}
        </HStack>
      </FormControl>
      {videoSrc && <StyledVideo src={videoSrc} controls muted />}
    </>
  );
};
